import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ONBOARDING_PHASE_LOCATION_VALUES,
  OnboardingPhase,
} from "@forgepoint/shared";
import { useOnboardingStore } from "@/modules/onboarding/onboardingStore";
import {
  formatDateOnly,
  parseDateOnly,
} from "@/modules/onboarding/onboardingDates";
import { PHASE_LOCATION_LABELS } from "@/modules/onboarding/phaseLocation";
import { parseEnumValue } from "@/lib/utils";

interface PhaseEditorRowProps {
  phase: OnboardingPhase;
  isFirst: boolean;
  isLast: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

/**
 * One editable schedule phase: title, location, and start date. The title
 * saves on blur; the location and start date save on selection, so no
 * partial value ever reaches the API.
 */
export function PhaseEditorRow({
  phase,
  isFirst,
  isLast,
  onMoveUp,
  onMoveDown,
}: PhaseEditorRowProps) {
  const phaseState = useOnboardingStore((state) =>
    state.phaseStateById.get(phase.id),
  );
  const updatePhase = useOnboardingStore((state) => state.updatePhase);
  const deletePhase = useOnboardingStore((state) => state.deletePhase);

  const [title, setTitle] = useState(phase.title);

  useEffect(() => {
    setTitle(phase.title);
  }, [phase.title]);

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border p-2">
      <div className="flex flex-col">
        <Button
          variant="ghost"
          size="icon"
          className="h-5 w-5"
          disabled={isFirst}
          onClick={onMoveUp}
        >
          <ArrowUp className="h-3 w-3" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-5 w-5"
          disabled={isLast}
          onClick={onMoveDown}
        >
          <ArrowDown className="h-3 w-3" />
        </Button>
      </div>
      <Input
        className="w-44 flex-1"
        value={title}
        placeholder="Phase title"
        onChange={(event) => setTitle(event.target.value)}
        onBlur={() => {
          if (title.trim() && title !== phase.title) {
            updatePhase(phase.id, { title: title.trim() });
          }
        }}
      />
      <Select
        value={phase.location}
        onValueChange={(value) => {
          const parsed = parseEnumValue(
            ONBOARDING_PHASE_LOCATION_VALUES,
            value,
          );
          if (parsed && parsed !== phase.location) {
            updatePhase(phase.id, { location: parsed });
          }
        }}
      >
        <SelectTrigger className="w-32">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {ONBOARDING_PHASE_LOCATION_VALUES.map((value) => (
            <SelectItem key={value} value={value}>
              {PHASE_LOCATION_LABELS[value]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <DatePicker
        className="w-40"
        value={parseDateOnly(phase.startDate)}
        onSelect={(date) => {
          if (date) {
            updatePhase(phase.id, { startDate: formatDateOnly(date) });
          }
        }}
      />
      <div className="ml-auto flex items-center gap-1">
        {phaseState?.isSaving && (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        )}
        {phaseState?.error && (
          <span className="text-xs text-destructive">{phaseState.error}</span>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-destructive"
          onClick={() => deletePhase(phase.id)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
